import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function TermsOfServicePage() {
  const lastUpdated = "August 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-9 h-9 rounded-md flex items-center justify-center overflow-hidden">
                <Logo className="w-9 h-9" alt="" />
              </div>
              <span className="font-medium text-lg">No Half Days</span>
            </Link>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="page-title mb-2">Terms of service</h1>
          <p className="text-muted-foreground text-lg mb-8">Last updated: {lastUpdated}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of No Half Days (the "Service"), operated by Your Figment Pty Ltd (ABN to be provided) ("we", "us", or "our"), a company registered in Australia.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              No Half Days is a job management and equipment tracking platform designed for freelancers, videographers, photographers, and creative professionals. The Service allows you to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Create and manage job estimates</li>
              <li>Track equipment inventory and calculate ROI</li>
              <li>Manage client relationships</li>
              <li>Generate professional PDF documents</li>
              <li>Export or connect your jobs to accounting software (e.g., Xero)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>Important:</strong> No Half Days is not accounting software. We do not generate invoices or provide accounting services. The Service is designed to help you create estimates and track your work, which you can then export to your chosen accounting software for invoicing and financial management.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Account Registration</h2>
            <p className="text-muted-foreground leading-relaxed">
              To use the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Promptly update your account information if it changes</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You must be at least 18 years old to create an account and use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Subscription and Payment</h2>
            
            <h3 className="text-xl font-medium mb-3">4.1 Free Trial</h3>
            <p className="text-muted-foreground leading-relaxed">
              New users receive a 30-day free trial with full access to all features. No payment information is required during the trial period.
            </p>

            <h3 className="text-xl font-medium mb-3 mt-6">4.2 Subscription Plans</h3>
            <p className="text-muted-foreground leading-relaxed">
              After the trial period, continued access requires a paid subscription. Current pricing and plan details are available on our website. We reserve the right to modify pricing with 30 days' notice to existing subscribers.
            </p>

            <h3 className="text-xl font-medium mb-3 mt-6">4.3 Payment Processing</h3>
            <p className="text-muted-foreground leading-relaxed">
              Payments are processed through Polar (polar.sh), our Merchant of Record. By subscribing, you also agree to Polar's terms of service. Polar handles all payment processing, invoicing, and tax compliance on our behalf.
            </p>

            <h3 className="text-xl font-medium mb-3 mt-6">4.4 Automatic Renewal</h3>
            <p className="text-muted-foreground leading-relaxed">
              Subscriptions automatically renew at the end of each billing period unless cancelled. You may cancel your subscription at any time through your account settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Refund Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong>All sales are final. We do not offer refunds for subscription payments.</strong>
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We encourage you to make full use of our 30-day free trial to evaluate the Service before subscribing. If you cancel your subscription, you will retain access until the end of your current billing period.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              For the complete refund policy, please see our <Link to="/refund-policy" className="text-primary hover:underline">Refund Policy</Link> page.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Scrape, copy, or harvest data from the Service</li>
              <li>Resell, sublicense, or commercially exploit the Service</li>
              <li>Impersonate any person or entity</li>
              <li>Use the Service to send spam or unsolicited communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Intellectual Property</h2>
            
            <h3 className="text-xl font-medium mb-3">7.1 Our Property</h3>
            <p className="text-muted-foreground leading-relaxed">
              The Service, including its original content, features, and functionality, is owned by Your Figment Pty Ltd and is protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-medium mb-3 mt-6">7.2 Your Content</h3>
            <p className="text-muted-foreground leading-relaxed">
              You retain ownership of all content you upload to the Service ("Your Content"). By uploading content, you grant us a limited license to store, display, and process Your Content solely to provide the Service to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Data and Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your use of the Service is also governed by our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, which describes how we collect, use, and protect your personal information.
            </p>
            
            <h3 className="text-xl font-medium mb-3 mt-6">8.1 Data Deletion</h3>
            <p className="text-muted-foreground leading-relaxed">
              When you delete your account, all your data is permanently and immediately deleted from our systems. This includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Personal information (name, email, profile data)</li>
              <li>All job estimates and client information</li>
              <li>Equipment records and business settings</li>
              <li>Any uploaded files or images</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>This deletion is permanent and cannot be recovered.</strong> We recommend exporting any data you wish to keep before deleting your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service may integrate with third-party services (e.g., Xero, Polar, Google). Your use of these integrations is subject to the respective third-party terms of service. We are not responsible for the availability, accuracy, or content of third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>The Service is not accounting software and does not generate invoices</li>
              <li>The Service is not a substitute for professional accounting or legal advice</li>
              <li>You are responsible for verifying the accuracy of estimates and calculations</li>
              <li>We do not guarantee any specific business outcomes from using the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOUR FIGMENT PTY LTD SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Our total liability for any claims arising from your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless Your Figment Pty Ltd and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination, your right to use the Service will immediately cease.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You may terminate your account at any time by deleting your account through the Settings page or by contacting us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by email or through the Service. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">15. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms are governed by and construed in accordance with the laws of Australia. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Australia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">16. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-foreground font-medium">Your Figment Pty Ltd</p>
              <p className="text-muted-foreground">Email: crew@nohalfdays.com</p>
              <p className="text-muted-foreground">Website: nohalfdays.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Your Figment Pty Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/refund-policy" className="text-muted-foreground hover:text-foreground transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
